export type LandingLanguage = "it" | "en" | "es";

export type AuthMode = "demo" | "login" | "signup" | "brand";

export const landingLanguages: Array<{ code: LandingLanguage; label: string }> = [
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
];

export const landingCopy = {
  it: {
    nav: { how: "Per te", brands: "Per le aziende", access: "Prova" },
    hero: {
      title: ["Tu sei", "la tua taglia."],
      description: "La tua taglia personale, salvata in un QR da mostrare nelle boutique e ai brand aderenti.",
      action: "Prova FitYouX",
      imageAlt: "Etichetta verticale per abbigliamento cucita su cotone nero",
      labelAria: "Etichetta taglie con FitYouX selezionata",
      qrAria: "QR code per fityoux.com",
    },
    how: {
      aria: "Come funziona FitYouX",
      scanTitle: "Due foto, una volta",
      scanBody: "Una foto frontale e una laterale, guidate dal telefono.",
      styleTitle: "Scegli la vestibilità",
      styleBody: "Aderente, regolare, comoda o oversize. Le misure del corpo non cambiano.",
      controlTitle: "Mostra il tuo QR",
      controlBody: "In boutique autorizzi l’accesso temporaneo alle tue misure del profilo.",
    },
    project: {
      notShopTitle: "FitYouX non è un negozio.",
      notShopBody: "Non vende capi e non ha un catalogo. È la tua taglia personale, pronta da usare dove i brand adottano FitYouX.",
      userTitle: "Per te",
      userBody: "Crei una volta il tuo profilo corporeo. Quando entri in boutique scegli il capo, poi mostri il QR quando ti chiedono la taglia.",
      userSteps: ["Crea la tua taglia con il telefono", "Scegli come preferisci la vestibilità", "Mostra il QR FitYouX in boutique"],
      brandTitle: "Per le aziende",
      brandBody: "Il QR autorizza la lettura delle misure del profilo del cliente. Il brand le confronta con il proprio cartamodello e individua la vestibilità corretta.",
      brandSteps: ["Scansiona il QR del cliente", "Ricevi misure e preferenza autorizzate", "Applica i dati al tuo capo o cartamodello"],
      bridgeTitle: "Alla domanda «Che taglia?»",
      bridgeBody: "Il cliente mostra il QR e risponde: «FitYouX». La boutique vede subito le misure necessarie, senza conversioni tra Paesi.",
    },
    global: {
      title: "Una taglia. Ovunque.",
      body: "Crea FitYouX una volta e usala con ogni brand aderente, in ogni Paese. Niente più conversioni tra S, M, 42 o 10.",
      account: "ACCOUNT",
      fit: "LA TUA FITYOUX",
      brands: "OGNI BRAND ADERENTE",
      today: "OGGI",
      different: "Taglie diverse in ogni Paese",
      markets: [
        { name: "USA", example: "M" },
        { name: "EUROPA", example: "40" },
        { name: "CINA", example: "170/88A" },
      ],
      withFitYouX: "CON FITYOUX",
      personal: "La tua taglia personale",
      network: "La stessa FitYouX con ogni brand aderente",
    },
    brands: {
      title: "Più fedeltà. Meno resi.",
      body: "Ogni brand aderente riconosce la stessa FitYouX. Il cliente riordina più facilmente e il brand può ridurre i resi evitabili.",
      fit: "FitYouX personale",
      preference: "Stile scelto per ogni articolo",
      repeat: "Riordino più semplice",
      returns: "Meno resi evitabili",
      productionTitle: "Dalla taglia al capo.",
      productionBody: "Il brand adatta il proprio cartamodello alle misure FitYouX e alla vestibilità scelta. La sartoria riceve le misure finali per tagliare e cucire il capo.",
      productionSteps: ["Misure FitYouX", "Cartamodello del brand", "Misure finali del capo", "Sartoria e confezione"],
      action: "Scopri FitYouX per i brand",
    },
    access: {
      title: "La tua taglia inizia qui.",
      body: "Prova il percorso cliente dal telefono o apri la dashboard dimostrativa del brand.",
      tabs: { demo: "Demo", login: "Accedi", signup: "Registrati", brand: "Brand" },
      headings: { demo: "Prova FitYouX.", login: "Bentornato.", signup: "Crea la tua FitYouX.", brand: "Crea lo studio del brand." },
      descriptions: {
        demo: "Prova il percorso: crea la taglia, scegli la vestibilità e mostra il QR.",
        login: "La tua taglia è pronta quando lo sei tu.",
        signup: "Inizia con due foto e perfeziona la tua taglia con ogni capo.",
        brand: "Accedi allo spazio aziendale e consulta le misure condivise dai clienti.",
      },
      fields: { name: "Nome e cognome", brand: "Nome del brand", email: "Email", password: "Password" },
      consentBefore: "Accetto i ",
      terms: "termini del progetto pilota",
      consentMiddle: " e ho letto l’",
      privacy: "informativa privacy",
      consentAfter: ".",
      buttons: { demo: "Avvia demo mobile", brandDemo: "Apri dashboard aziendale", login: "Accedi", signup: "Crea la mia FitYouX", brand: "Crea lo studio del brand", busy: "Attendi…" },
      error: "Non è stato possibile continuare",
    },
    footer: { line: "La taglia personale universale per la moda.", privacy: "Privacy", terms: "Termini" },
  },
  en: {
    nav: { how: "For you", brands: "For companies", access: "Try it" },
    hero: {
      title: ["You are", "your size."],
      description: "Your personal size, saved in one QR to show in participating boutiques and to brands.",
      action: "Try FitYouX",
      imageAlt: "A vertical woven garment label stitched into black cotton fabric",
      labelAria: "Size label with FitYouX selected",
      qrAria: "QR code to fityoux.com",
    },
    how: {
      aria: "How FitYouX works",
      scanTitle: "Two photos, once",
      scanBody: "One front and one side photo, guided by your phone.",
      styleTitle: "Choose your preferred fit",
      styleBody: "Fitted, regular, relaxed or oversized. Your body measurements stay the same.",
      controlTitle: "Show your QR",
      controlBody: "In store, you authorise temporary access to your body measurements.",
    },
    project: {
      notShopTitle: "FitYouX is not a shop.",
      notShopBody: "It sells no clothes and has no catalog. It is your personal size, ready wherever brands adopt FitYouX.",
      userTitle: "For you",
      userBody: "Create your body profile once. In a boutique, choose the garment and show your QR when the team asks for your size.",
      userSteps: ["Create your size on your phone", "Choose how you prefer clothes to fit", "Show your FitYouX QR in store"],
      brandTitle: "For companies",
      brandBody: "The QR authorises access to the customer’s body measurements. The brand compares them with its own pattern to identify the right fit.",
      brandSteps: ["Scan the customer’s QR", "Receive authorised measurements and fit", "Apply the data to your garment or pattern"],
      bridgeTitle: "When they ask: “What size?”",
      bridgeBody: "The customer shows the QR and says: “FitYouX.” The boutique immediately sees the required measurements, without country conversions.",
    },
    global: {
      title: "One size. Everywhere.",
      body: "Create FitYouX once and use it with every participating brand, in every country. No more converting S, M, 42 or 10.",
      account: "ACCOUNT",
      fit: "YOUR FITYOUX",
      brands: "EVERY PARTICIPATING BRAND",
      today: "TODAY",
      different: "Different sizes in every country",
      markets: [
        { name: "USA", example: "M" },
        { name: "EUROPE", example: "40" },
        { name: "CHINA", example: "170/88A" },
      ],
      withFitYouX: "WITH FITYOUX",
      personal: "Your personal size",
      network: "The same FitYouX with every participating brand",
    },
    brands: {
      title: "More loyalty. Fewer returns.",
      body: "Every participating brand recognises the same FitYouX. Customers can reorder more easily and brands can reduce avoidable returns.",
      fit: "Personal FitYouX",
      preference: "Fit choice for every item",
      repeat: "Easier repeat purchase",
      returns: "Fewer avoidable returns",
      productionTitle: "From size to garment.",
      productionBody: "The brand adapts its pattern to the FitYouX measurements and chosen fit. The tailor receives the final measurements to cut and sew the garment.",
      productionSteps: ["FitYouX measurements", "Brand pattern", "Final garment specs", "Tailoring and production"],
      action: "FitYouX for brands",
    },
    access: {
      title: "Your size starts here.",
      body: "Try the customer journey on your phone or open the brand demonstration dashboard.",
      tabs: { demo: "Demo", login: "Sign in", signup: "Register", brand: "Brand" },
      headings: { demo: "Try FitYouX.", login: "Welcome back.", signup: "Create your FitYouX.", brand: "Create a brand studio." },
      descriptions: {
        demo: "Try the journey: create your size, choose the fit and show the QR.",
        login: "Your size is ready when you are.",
        signup: "Start with two photos and refine your size with every garment.",
        brand: "Open the company workspace and view measurements shared by customers.",
      },
      fields: { name: "Full name", brand: "Brand name", email: "Email", password: "Password" },
      consentBefore: "I accept the ",
      terms: "pilot terms",
      consentMiddle: " and have read the ",
      privacy: "privacy notice",
      consentAfter: ".",
      buttons: { demo: "Start mobile demo", brandDemo: "Open brand dashboard", login: "Sign in", signup: "Create my FitYouX", brand: "Create brand studio", busy: "Please wait…" },
      error: "Unable to continue",
    },
    footer: { line: "The universal personal size for fashion.", privacy: "Privacy", terms: "Terms" },
  },
  es: {
    nav: { how: "Para ti", brands: "Para empresas", access: "Probar" },
    hero: {
      title: ["Tú eres", "tu talla."],
      description: "Tu talla personal, guardada en un QR para mostrar en boutiques y marcas adheridas.",
      action: "Probar FitYouX",
      imageAlt: "Etiqueta vertical de ropa cosida sobre algodón negro",
      labelAria: "Etiqueta de tallas con FitYouX seleccionada",
      qrAria: "Código QR para fityoux.com",
    },
    how: {
      aria: "Cómo funciona FitYouX",
      scanTitle: "Dos fotos, una vez",
      scanBody: "Una foto frontal y otra lateral, guiadas desde tu móvil.",
      styleTitle: "Elige el ajuste",
      styleBody: "Entallado, regular, cómodo u oversize. Tus medidas corporales no cambian.",
      controlTitle: "Muestra tu QR",
      controlBody: "En la boutique autorizas el acceso temporal a tus medidas del perfil.",
    },
    project: {
      notShopTitle: "FitYouX no es una tienda.",
      notShopBody: "No vende ropa ni tiene catálogo. Es tu talla personal, lista para usar donde las marcas adopten FitYouX.",
      userTitle: "Para ti",
      userBody: "Creas una vez tu perfil corporal. En la boutique eliges la prenda y muestras el QR cuando te preguntan la talla.",
      userSteps: ["Crea tu talla desde el móvil", "Elige cómo prefieres el ajuste", "Muestra tu QR FitYouX en la boutique"],
      brandTitle: "Para las empresas",
      brandBody: "El QR autoriza el acceso a las medidas del perfil del cliente. La marca las compara con su propio patrón para identificar el ajuste correcto.",
      brandSteps: ["Escanea el QR del cliente", "Recibe medidas y ajuste autorizados", "Aplica los datos a tu prenda o patrón"],
      bridgeTitle: "Cuando preguntan «¿Qué talla?»",
      bridgeBody: "El cliente muestra el QR y responde: «FitYouX». La boutique ve enseguida las medidas necesarias, sin conversiones entre países.",
    },
    global: {
      title: "Una talla. En todas partes.",
      body: "Crea FitYouX una vez y úsala con cada marca adherida, en cualquier país. Olvídate de convertir S, M, 42 o 10.",
      account: "CUENTA",
      fit: "TU FITYOUX",
      brands: "CADA MARCA ADHERIDA",
      today: "HOY",
      different: "Tallas diferentes en cada país",
      markets: [
        { name: "EE. UU.", example: "M" },
        { name: "EUROPA", example: "40" },
        { name: "CHINA", example: "170/88A" },
      ],
      withFitYouX: "CON FITYOUX",
      personal: "Tu talla personal",
      network: "La misma FitYouX con cada marca adherida",
    },
    brands: {
      title: "Más fidelidad. Menos devoluciones.",
      body: "Cada marca adherida reconoce la misma FitYouX. El cliente puede repetir su compra con facilidad y la marca reducir devoluciones evitables.",
      fit: "FitYouX personal",
      preference: "Ajuste elegido para cada prenda",
      repeat: "Recompra más sencilla",
      returns: "Menos devoluciones evitables",
      productionTitle: "De la talla a la prenda.",
      productionBody: "La marca adapta su patrón a las medidas FitYouX y al ajuste elegido. El taller recibe las medidas finales para cortar y confeccionar la prenda.",
      productionSteps: ["Medidas FitYouX", "Patrón de la marca", "Medidas finales", "Taller y confección"],
      action: "FitYouX para marcas",
    },
    access: {
      title: "Tu talla empieza aquí.",
      body: "Prueba el recorrido del cliente desde tu móvil o abre el panel demostrativo de la marca.",
      tabs: { demo: "Demo", login: "Acceder", signup: "Registro", brand: "Marca" },
      headings: { demo: "Prueba FitYouX.", login: "Te damos la bienvenida.", signup: "Crea tu FitYouX.", brand: "Crea el estudio de tu marca." },
      descriptions: {
        demo: "Prueba el recorrido: crea tu talla, elige el ajuste y muestra el QR.",
        login: "Tu talla está lista cuando tú lo estés.",
        signup: "Empieza con dos fotos y perfecciona tu talla con cada prenda.",
        brand: "Abre el espacio para empresas y consulta las medidas compartidas por los clientes.",
      },
      fields: { name: "Nombre completo", brand: "Nombre de la marca", email: "Email", password: "Contraseña" },
      consentBefore: "Acepto los ",
      terms: "términos del piloto",
      consentMiddle: " y he leído el ",
      privacy: "aviso de privacidad",
      consentAfter: ".",
      buttons: { demo: "Iniciar demo móvil", brandDemo: "Abrir panel de la marca", login: "Acceder", signup: "Crear mi FitYouX", brand: "Crear estudio de marca", busy: "Espera…" },
      error: "No ha sido posible continuar",
    },
    footer: { line: "La talla personal universal para la moda.", privacy: "Privacidad", terms: "Términos" },
  },
} as const;

export function detectLandingLanguage(): LandingLanguage {
  if (typeof window === "undefined") return "en";
  const saved = window.localStorage.getItem("fityoux-language");
  if (saved === "it" || saved === "en" || saved === "es") return saved;
  const browser = window.navigator.language.toLowerCase();
  if (browser.startsWith("it")) return "it";
  if (browser.startsWith("es")) return "es";
  return "en";
}
